// Drift Auditor -- 100% stacked area of how the UNCHECKED book's weights drift.

import React, { useMemo } from 'react';
import {
    Area,
    AreaChart,
    CartesianGrid,
    Legend,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';

import type { TimeSeriesPoint } from '../types';

const PALETTE = [
    '#6366f1', '#0ea5e9', '#10b981', '#f59e0b', '#f43f5e',
    '#8b5cf6', '#14b8a6', '#eab308', '#ec4899', '#64748b',
];

interface Props {
    data: TimeSeriesPoint[];
    tickers: string[];
}

export const WeightDriftChart: React.FC<Props> = ({ data, tickers }) => {
    // Flatten the drifted_weights map into top-level keys for Recharts.
    const chartData = useMemo(
        () =>
            data.map((p) => ({
                date: p.date,
                ...p.drifted_weights,
            })),
        [data],
    );

    return (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5">
            <h3 className="text-base font-bold text-slate-800">Allocation Drift (Unchecked)</h3>
            <p className="text-xs text-slate-500 mt-0.5 mb-3">
                Without rebalancing, winners crowd out everything else.
            </p>
            <div className="h-64 w-full">
                <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={chartData} margin={{ top: 5, right: 8, left: 4, bottom: 0 }} stackOffset="expand">
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                        <XAxis
                            dataKey="date"
                            tick={{ fontSize: 11, fill: '#94a3b8' }}
                            minTickGap={48}
                            tickLine={false}
                        />
                        <YAxis
                            tickFormatter={(v: number) => `${(v * 100).toFixed(0)}%`}
                            tick={{ fontSize: 11, fill: '#94a3b8' }}
                            width={44}
                            tickLine={false}
                            axisLine={false}
                        />
                        <Tooltip
                            formatter={(v: number) => `${v.toFixed(2)}%`}
                            contentStyle={{ borderRadius: 12, border: '1px solid #e2e8f0', fontSize: 12 }}
                        />
                        <Legend wrapperStyle={{ fontSize: 12 }} />
                        {tickers.map((t, i) => (
                            <Area
                                key={t}
                                type="monotone"
                                dataKey={t}
                                name={t}
                                stackId="1"
                                stroke={PALETTE[i % PALETTE.length]}
                                fill={PALETTE[i % PALETTE.length]}
                                fillOpacity={0.75}
                            />
                        ))}
                    </AreaChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};
